import { useMutation } from "@tanstack/react-query";
import { useContext, useState } from "react";
import {
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import AuthContext from "../../context/authContext";
import { AuthContextInfo } from "../../context/types/authContextType";
import { AUTH_STATUS } from "../../context/enums/authEnums";
import { AuthorizeResponse } from "../../Graphql/types/auth";
import { fetchGraphQL } from "../../Graphql/utils";
import { authorizeQuery } from "../../Graphql/querries/authQuery";
import { isSuperAdminRole } from "../../Utils/authUtils";
import { MembershipStatus } from "../../types/membership";

export const SUPER_ADMIN_NOT_ALLOWED_ERROR = "SUPER_ADMIN_NOT_ALLOWED";

const useAuthorize = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { authContextInfo, updateAuthContextInfo } = useContext(AuthContext);
  const [authContextInfoLocal, updateAuthContextInfoLocal] =
    useState<AuthContextInfo>({
      auth: { status: AUTH_STATUS.UNAUTHENTICATED },
      membership: MembershipStatus.NONE
    });

  return useMutation<AuthorizeResponse, Error>({
    mutationKey: ["authorize"],
    // Configure `retry` / `retryDelay` on this mutation if needed—not manual loops in callers.
    retry: 0,
    mutationFn: () => fetchGraphQL(authorizeQuery, { input: {} }, setAlert),
    onSuccess: (response) => {
      const { authorized, code, membership, role } = response.authorize;

      if (
        code === API_RESPONSE_CODE.OK &&
        authorized &&
        isSuperAdminRole(role)
      ) {
        updateAuthContextInfo({
          ...authContextInfo,
          auth: { status: AUTH_STATUS.UNAUTHENTICATED }
        });
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: "Super admins must use the admin portal. This app is for end users only." as ALERT_BANNER_CODE
        });
        throw new Error(SUPER_ADMIN_NOT_ALLOWED_ERROR);
      }

      if (code === API_RESPONSE_CODE.OK && authorized) {
        updateAuthContextInfoLocal({
          ...authContextInfoLocal,
          auth: {
            authorized,
            status: AUTH_STATUS.AUTHENTICATED
          },
          membership
        });
      } else {
        updateAuthContextInfoLocal({
          ...authContextInfoLocal,
          auth: {
            authorized,
            status: AUTH_STATUS.UNAUTHENTICATED
          },
          membership: MembershipStatus.NONE
        });
        throw new Error(code);
      }
    },
    onError: () => {
      // Do not rethrow: the mutation promise already rejects with the error.
      // Rethrowing here can cause an uncaught promise rejection.
    }
  });
};

export default useAuthorize;
